<template>
	<div class="flex h-full">
		<div class="bullet-form flex flex-col justify-between overflow-auto p-5 hover:overflow-visible">
			<div>
				<div class="flex items-center gap-2">
					<div class="won-blue text-base font-medium">Bullet Generator</div>

					<span
						v-if="showFreeUsesBadge"
						class="rounded-md bg-[#F1F1F1] px-2 py-1 text-xs font-medium whitespace-nowrap text-won-dark-blue"
					>
						{{ freeUsesLabel }}
					</span>
				</div>

				<tabs
					small
					:tabs="tabs.filter(el => el.valid)"
					:current="currentTab"
					class="mt-4"
					@changed="(tab) => (currentTab = tab)"
				/>
				<resume-form
					:key="currentTab"
					:form="currentTab"
					class="mt-5"
					:default-values="defaultValues"
					:is_resume_builder="true"
					@resume:loading="handleLoading"
					@resume:received="handleReceived"
				/>
			</div>
		</div>

		<div class="bullet-results z-10 size-full">
			<div class="flex items-center justify-between p-5">
				<div
					v-if="!loading && !!resultsData?.length"
					class="won-blue text-base font-medium"
				>
					Generated Bullet Points
				</div>
				<div v-else />

				<svg
					class="size-5 cursor-pointer"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					@click="handleClose"
				>
					<path
						d="M6 6L18 18M6 18L18 6L6 18Z"
						stroke="#96AAB7"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</svg>
			</div>

			<div
				v-if="loading"
				class="z-20 flex flex-col items-center justify-center"
				style="color: #505d68; height: 70vh"
			>
				<bullet-loading class="spin mb-4" />

				<div class="text-xl">Generating results...</div>

				<div class="mt-3 text-center text-sm">
					Hold tight! Your results will
					<br />
					be ready in a couple seconds.
				</div>
			</div>

			<div
				v-if="!loading && !resultsData?.length"
				class="flex flex-col items-center justify-center"
				style="color: #505d68; height: 70vh"
			>
				<div class="text-xl">No results yet!</div>

				<div class="mt-3 text-center text-sm">
					To see results, fill in the
					<br />
					required fields and hit
					<br />
					‘Generate.’
				</div>
			</div>

			<div
				v-if="!loading && !!resultsData?.length"
				class="px-5"
			>
				<div
					v-for="item of resultsData"
					:key="item.id"
					class="bullet-item-result flex"
				>
					<added
						v-if="addedIds[`${section}-${index}`]?.includes(item.id)"
						class="mt-px mr-2.5 cursor-pointer"
						style="min-width: 20px"
					/>
					<add-icon
						v-else
						class="mt-px mr-2.5 cursor-pointer"
						style="min-width: 20px"
						@click="() => handleAdd(item)"
					/>

					<div
						class="won-blue text-sm"
						v-html="item.completionHtml"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { v4 as uuid } from 'uuid';

import { decodeHtml, extractContent } from '../../utils/helpers';
import { safeForceFlush } from '../../utils/tiptap-error-suppression';

import ResumeForm from '../../forms/resume-form';
import Tabs from '../tabs';

import BulletLoading from '../../icons/bullet-loading';
import AddIcon from '../../icons/add';
import Added from '../../icons/added';

import { useBuilder } from '../../store/builder.store';
import { useBulletGenerator } from '../../store/bullet-store';
import { useOnboardingStore } from '../../store/onboarding.store';
import { useSubscriptionPlanStore } from '../../store/subscription.store';
import { useFeatureUsageStore } from '../../store/feature-usage.store';

import { useTiptapStore } from '../../store/tiptap.store';

import { resumeResultsMock }  from '../../utils/mock';

export default {
	components: {
		ResumeForm,
		Tabs,
		BulletLoading,
		AddIcon,
		Added,
	},

	setup() {
		const addedIds = ref({});
		const loading = ref(false);
		const results = ref(null);

		const storageKey = 'WONSULTING-resume-selected-tab';
		const fromStorage = localStorage.getItem(storageKey);

		const currentTab = ref(fromStorage || 'resume-rewrite-form');

		const builder = useBuilder();
		const bulletGenerator = useBulletGenerator();
		const { section, index } = storeToRefs(bulletGenerator);

		const tiptapStore = useTiptapStore();
		const { editor } = storeToRefs(tiptapStore);

		const onboardingStore = useOnboardingStore();
		const { isOnboarding } = storeToRefs(onboardingStore);
    
		const subscriptionPlanStore = useSubscriptionPlanStore();

		// Read isSubscribe off the store (it's reactive) instead of storeToRefs: the store
		// exposes a plain `stripeKey` that is undefined when VITE_STRIPE_KEY is unset
		// (e.g. in CI), which makes storeToRefs() throw before this panel can mount.
		const isSubscribe = computed(() => subscriptionPlanStore.isSubscribe);
    
		const userFeatureUsageStore = useFeatureUsageStore();
		const { userFeatureUsage } = storeToRefs(userFeatureUsageStore);

		// EN-811 free-tier credit counter. Read the row off userFeatureUsage directly:
		// fetchFeatureUsage() silently falls back to the resume_upload row when a feature
		// is missing, which would show someone else's count.
		const bulletUsage = computed(
			() => userFeatureUsage.value?.find((feature) => feature.name === 'bullet_generation_tool') ?? null,
		);

		const showFreeUsesBadge = computed(
			() => !isSubscribe.value && bulletUsage.value != null,
		);

		const freeUsesLeft = computed(() => Math.max(0, bulletUsage.value?.usage ?? 0));

		const freeUsesLabel = computed(
			() => `${freeUsesLeft.value} Free ${freeUsesLeft.value === 1 ? 'Use' : 'Uses'} Left`,
		);

		const defaultValues = computed(() => {
			// if onboarding show different values
			const defaultRole = isOnboarding.value ? 'Designer' : (bulletGenerator?.title ? decodeHtml(extractContent(bulletGenerator?.title)) : null);
			const defaultWorkExperience = isOnboarding.value ? 'designed a mobile app to help customers retrieve their returned items from a store' : null;

			return {
				role: defaultRole,
				current_points: defaultWorkExperience,
			};
		});

		const markdownBoldToHtml = (text) => text
			.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
			.replace(/\*\*/g, '');

		const markdownBoldToTiptapContent = (text) => {
			return text.split(/(\*\*.*?\*\*)/g).filter(Boolean).map((part) => {
				const bold = /^\*\*(.*)\*\*$/.exec(part);
				return bold
					? { type: 'text', text: bold[1], marks: [{ type: 'bold' }] }
					: { type: 'text', text: part.replace(/\*\*/g, '') };
			});
		};

		const resultsData = computed(() => {
			if (isOnboarding.value) {return resumeResultsMock;}

			const data = (results.value?.completion || '')
				.split('\n')
				.filter(Boolean)
				.map((e) => e.replace(/^\s*(?:\d+[.)]\s*|[-•*]\s*)/, ''));

			return data.map((e) => ({
				completion: e,
				completionHtml: markdownBoldToHtml(e),
				id: uuid(),
			}));
		});

		onMounted(() => {
			if (!isSubscribe.value) {
				userFeatureUsageStore.refresh().catch(() => {
					// Keep the cached count; it is refreshed again after each generation.
				});
			}

			document.addEventListener('keydown', (e) => {
				if (isOnboarding.value) {return;}
				if (e.key === 'Escape') {bulletGenerator.reset();}
			});
		});

		return {
			editor,
			addedIds,
			currentTab,
			loading,
			results,

			section,
			index,
			defaultValues,
			resultsData,

			builder,
			bulletGenerator,

			storageKey,

			isSubscribe,
			userFeatureUsage,
			showFreeUsesBadge,
			freeUsesLabel,
			markdownBoldToTiptapContent,
		};
	},

	data: () => ({
		tabs: [
			{ name: 'Create new points', id: 'resume-form', valid: false },
			{ name: 'Create Bullet Points', id: 'resume-rewrite-form', valid: true },
		],
	}),

	watch: {
		currentTab() {
			this.results = [];
			this.loading = false;

			localStorage.setItem(this.storageKey, this.currentTab);
		},
	},

	methods: {
		handleLoading(loading) {
			this.loading = loading;

			if (loading) {
				this.results = [];
			}
		},

		handleReceived(data) {
			this.results = data;
		},
      
		createListWith(content) {
			return {
				type: 'bulletList',
				content: [{
					type: 'listItem',
					content: [
						{
							type: 'paragraph',
							content: this.markdownBoldToTiptapContent(content),
						}
					]
				}]
			};
		},

		async handleAdd({ id, completion }) {
			const key = `${this.section}-${this.index}`;

			if (!this.addedIds?.[key]?.length) {
				this.addedIds[key] = [];
			}

			this.addedIds[key].push(id);

			const editorContentJSON = this.editor.getJSON();
			const bulletListIndex = editorContentJSON.content.findLastIndex((contentPiece) => {
				return contentPiece.type === 'bulletList';
			});

			const newContent = editorContentJSON;

			if (bulletListIndex !== -1) {
				const bulletList = editorContentJSON.content[bulletListIndex];

				bulletList.content.push({
					type: 'listItem',
					content: [
						{
							type: 'paragraph',
							content: this.markdownBoldToTiptapContent(completion),
						}
					]
				});

				newContent.content.splice(bulletListIndex, 1, bulletList);
			} else {
				newContent.content = [...newContent.content, this.createListWith(completion)];
			}

			try {
				const content = this.editor.isEmpty
					? { ...newContent, content: newContent.content.filter((item) => item.type !== 'paragraph') }
					: newContent;

				safeForceFlush(this.editor);
				this.editor.commands.setContent(content, true);
				this.editor.commands.blur();
				await this.$nextTick();
				this.editor.commands.focus();
			} catch (error) {
				if (!(error instanceof RangeError)) {
					throw error;
				}
			}
		},

		handleClose() {
			try {
				this.editor.commands.blur();
			} catch (error) {
				if (!(error instanceof RangeError)) {
					throw error;
				}
			}
			this.bulletGenerator.reset();
		},
	},
};
</script>

<style scoped>
.bullet-form {
  width: 326px;
  border-right: 1px solid #96aab7;
}

.bullet-results {
  width: 289px;
  z-index: 10;
}

.bullet-item-result:not(:last-of-type) {
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #dbdbdc;
}
</style>
